import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Currency, Insurance } from '@core/constants/data-types.constants';
import { Config } from '@core/models/config.model';
import { CurrencyExchange } from '@core/models/currency-exchange';
import { getProductAvailableIncreases } from '@core/models/increase.model';
import { LifePlan } from '@core/models/life-plan.model';
import { adjustValuesToCurrency, applyProductTemporalityConditionLimits, Product } from '@core/models/product.model';
import {
  convertTemporalityConditionToTemporality,
  getProductAvailableTemporalities,
  Temporality
} from '@core/models/temporality.model';
import { LanguageService } from '@i18n/services/language.service';
import { Store } from '@ngrx/store';
import { AppState } from '@redux/app.reducers';
import { Subscription } from 'rxjs';

@Component({
  selector: 'mfp-product-card',
  templateUrl: './product-card.component.html',
  styleUrls: ['./product-card.component.scss']
})
export class ProductCardComponent implements OnInit, OnDestroy {
  @Input() product!: Product;
  @Input() targetAge = 0;
  @Input() active = '';
  @Input() currencyExchange!: CurrencyExchange;
  @Input() insurance: Insurance = 'DEATH';
  @Output() productChange = new EventEmitter<Product>();

  lifePlan!: LifePlan;
  options: { key: string; value: string }[] = [];
  increaseOptions: { key: string; value: number }[] = [];
  allowedCurrencies: { key: string; value: string }[] = [];
  currencyPosition: 'left' | 'right' = 'right';

  storeSubscription$!: Subscription;

  constructor(private language: LanguageService, private store: Store<AppState>) {}

  ngOnInit(): void {
    this.storeSubscription$ = this.store.subscribe({
      next: state => {
        this.currencyPosition = state.config.currencyPosition;
        this.lifePlan = state.lifePlan.currentLifePlan;
        this.updateCurrencyOptions(state.config);
        this.updateIncreaseOptions();
        this.updateTemporalityOptions();
      }
    });
  }

  ngOnDestroy(): void {
    this.storeSubscription$.unsubscribe();
  }

  onChangeActive() {
    this.productChange.next(this.product);
  }

  getAmount(): number {
    let currencyAmount;
    if (this.product.type === 'normal') {
      currencyAmount = this.product.currentAssuredAmount;
    } else {
      currencyAmount = this.product.amountAtEndOfTemporality;
    }
    return currencyAmount.amount;
  }

  getCurrency(): Currency {
    if (this.product.type === 'normal') {
      return this.product.currentAssuredAmount.currency;
    } else {
      return this.product.amountAtEndOfTemporality.currency;
    }
  }

  onTemporalityChange(temporality: string) {
    this.product.selectedTemporality = temporality;
    const temporalitySelected =
      this.product.temporalityConditions.find(t => t.id === this.product.selectedTemporality) ||
      this.product.temporalityConditions[0];
    applyProductTemporalityConditionLimits(this.product, temporalitySelected);
    this.productChange.emit(this.product);
  }

  onIncreaseChange(increase: number) {
    const selectedIncrease = this.product.allowedIncreases?.find(
      inc => inc.percentage === increase && inc.currency === this.product.selectedCurrency
    );
    this.product.selectedIncrease = selectedIncrease;
    this.productChange.emit(this.product);
  }

  onCurrencyChange(currency: Currency) {
    adjustValuesToCurrency(this.product, currency, this.currencyExchange);
    if (this.product.type == 'normal') this.product.totalFee.currency = this.product.selectedCurrency;
    this.updateTemporalityOptions();
    this.productChange.emit(this.product);
    this.updateIncreaseOptions();
  }

  private updateCurrencyOptions(config: Config) {
    this.allowedCurrencies = config.currencies.filter(c =>
      this.product.allowedCurrencies.find(productC => c.value === productC)
    );
  }

  private updateIncreaseOptions() {
    const increases = getProductAvailableIncreases(this.product, this.product.selectedCurrency);
    this.increaseOptions = increases.map(increase => {
      return {
        key: increase.description,
        value: increase.percentage
      };
    });
    if (increases.length > 0 && this.product.selectedIncrease?.currency !== this.product.selectedCurrency) {
      this.product.selectedIncrease = increases[0];
    }
  }

  private updateTemporalityOptions() {
    const previousSelectedTemporalityId = this.product.selectedTemporality;
    const productAvailableTemporalities = getProductAvailableTemporalities(
      this.product,
      this.product.selectedCurrency,
      this.targetAge
    );
    this.options = productAvailableTemporalities.map(t => {
      return {
        key:
          t.type === 'until'
            ? this.language.instant(`PRODUCT_SELECT.TEMPORALITIES.${this.insurance}`, { years: t.value })
            : this.language.instant('PRODUCT_SELECT.TEMPORALITIES.UNTIL', { years: t.value }),
        value: t.id
      };
    });
    let newTemporality: Temporality | undefined;
    if (previousSelectedTemporalityId) {
      const previousTemporalityCondition = this.product.temporalityConditions.find(
        t => t.id === previousSelectedTemporalityId
      );
      if (previousTemporalityCondition) {
        const previousSelectedTemporality = convertTemporalityConditionToTemporality(previousTemporalityCondition);
        if (previousSelectedTemporality) {
          newTemporality = productAvailableTemporalities.find(t => {
            const isTheSame = t.id === previousSelectedTemporality.id;
            const isEqual =
              t.type === previousSelectedTemporality.type && t.value === previousSelectedTemporality.value;
            return isTheSame || isEqual;
          });
        }
      }
    }
    if (newTemporality) {
      this.product.selectedTemporality = newTemporality.id;
    } else if (productAvailableTemporalities.length > 0) {
      this.product.selectedTemporality = productAvailableTemporalities[0].id;
    }
    const temporalitySelected =
      this.product.temporalityConditions.find(t => t.id === this.product.selectedTemporality) ||
      this.product.temporalityConditions[0];
    applyProductTemporalityConditionLimits(this.product, temporalitySelected);
  }
}
